import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Window mode (Phase 3 "Liste", Phase 4 "Stapel").
 *
 * - `full`: normal main window with all chrome (sidebars, tabs).
 * - `list`: compact 412×612 window (Electron) / 412px column (Web) with chrome
 *   hidden, showing only the active project's task list.
 * - `stack`: even smaller window showing the project's open tasks as a card
 *   deck — one focused card, the next tasks peeking behind it.
 */
export type WindowMode = 'full' | 'list' | 'stack'

interface WindowModeContextValue {
  viewMode: WindowMode
  isListMode: boolean
  isStackMode: boolean
  /** `list` or `stack` — any mode with the compact (chrome-less) window shell. */
  isCompactMode: boolean
  /**
   * Scope of the compact list view: `null` = the whole project (all sections),
   * a section id = focus on that single section. Only meaningful while `list`.
   */
  listSectionId: string | null
  /** Enter list mode. Pass a section id to focus a single section. */
  switchToListMode: (sectionId?: string | null) => void
  /** Enter stack mode (always project scope — the deck has no section focus). */
  switchToStackMode: () => void
  switchToFullMode: () => void
  /** Toggle full↔list at project scope (used by ⌘L). */
  toggleListMode: () => void
  /** Toggle stack↔full (used by ⌘S). Entering stack from list works too. */
  toggleStackMode: () => void
  /**
   * "Always on top" preference for the compact window modes (list now, stack
   * later). A persisted preference: kept across restarts and full↔compact
   * switches, but only physically active while in a compact mode.
   */
  alwaysOnTop: boolean
  /** Toggle the compact "always on top" preference (drives the window via IPC). */
  toggleAlwaysOnTop: () => void
}

/** Exportiert für Stories, die einen festen Fenstermodus zeigen (`stories/page.tsx`). */
export const WindowModeContext = createContext<WindowModeContextValue | undefined>(undefined)

const STORAGE_KEY = 'window-view-mode'
const STORAGE_SECTION_KEY = 'window-list-section'
const STORAGE_ALWAYS_ON_TOP_KEY = 'window-always-on-top'

function readInitialMode(): WindowMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'list' || stored === 'stack' ? stored : 'full'
  } catch {
    return 'full'
  }
}

function readInitialSection(): string | null {
  try {
    return localStorage.getItem(STORAGE_SECTION_KEY) || null
  } catch {
    return null
  }
}

function readInitialAlwaysOnTop(): boolean {
  try {
    return localStorage.getItem(STORAGE_ALWAYS_ON_TOP_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Single source of truth for the window mode within a renderer window, shared
 * synchronously across MainLayout / Titlebar / ProjectPage. On Electron it drives
 * the real window resize via the `switch-view-mode` IPC (the Main process resizes
 * + restores bounds); on Web it only flips local state (CSS emulates the compact
 * column). The initial value is read synchronously from localStorage so the first
 * paint already gates the chrome — no flash.
 */
export function WindowModeProvider({ children }: { children: React.ReactNode }) {
  const [viewMode, setViewMode] = useState<WindowMode>(readInitialMode)
  const [listSectionId, setListSectionId] = useState<string | null>(readInitialSection)
  const [alwaysOnTop, setAlwaysOnTop] = useState<boolean>(readInitialAlwaysOnTop)

  const persist = useCallback((mode: WindowMode, sectionId: string | null) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
      if (sectionId) localStorage.setItem(STORAGE_SECTION_KEY, sectionId)
      else localStorage.removeItem(STORAGE_SECTION_KEY)
    } catch { /* ignore */ }
  }, [])

  // Drive the real window resize (Electron; web no-ops). Defer the resize (both
  // directions) until the renderer has painted the target layout HIDDEN — entering
  // list: compact chrome hidden; entering full: MainLayout holds the full UI hidden
  // until the resize settles. Otherwise the OS resizes while the old content is
  // still on screen (e.g. the list jumps to the top-left of the grown window). Two
  // rAFs guarantee the hidden state is painted before the resize.
  const invokeMode = useCallback((mode: WindowMode) => {
    if (!window.api) return
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.api?.invoke('switch-view-mode', mode)
    }))
  }, [])

  const apply = useCallback((mode: WindowMode, sectionId: string | null) => {
    setViewMode(mode)
    setListSectionId(sectionId)
    persist(mode, sectionId)
    invokeMode(mode)
  }, [persist, invokeMode])

  // Default scope = whole project (null). Pass a section id to focus one section.
  const switchToListMode = useCallback((sectionId: string | null = null) => apply('list', sectionId), [apply])
  // Stack is always project scope — the deck flattens all sections.
  const switchToStackMode = useCallback(() => apply('stack', null), [apply])
  const switchToFullMode = useCallback(() => apply('full', null), [apply])
  // ⌘L always toggles at project scope (no focused section).
  const toggleListMode = useCallback(() => {
    apply(viewMode === 'list' ? 'full' : 'list', null)
  }, [viewMode, apply])
  // ⌘S: enter stack from full/list, exit stack back to full.
  const toggleStackMode = useCallback(() => {
    apply(viewMode === 'stack' ? 'full' : 'stack', null)
  }, [viewMode, apply])

  // Toggle the compact "always on top" preference. Persist synchronously (so the
  // button paints the right colour on next mount) and drive the real window via
  // IPC. Unlike the resize IPC this needs no rAF defer — no layout swap happens.
  const toggleAlwaysOnTop = useCallback(() => {
    setAlwaysOnTop((prev) => {
      const next = !prev
      try { localStorage.setItem(STORAGE_ALWAYS_ON_TOP_KEY, String(next)) } catch { /* ignore */ }
      window.api?.invoke('set-always-on-top', next)
      return next
    })
  }, [])

  // Mirror the mode onto <html> so chrome rendered OUTSIDE this provider (the
  // Titlebar lives above it in the tree) can react purely via CSS. `list-mode`
  // doubles as the generic "compact chrome" flag (list AND stack) — all
  // existing compact styling keys off it; `stack-mode` adds the deck-specific
  // differences on top.
  useEffect(() => {
    document.documentElement.classList.toggle('list-mode', viewMode !== 'full')
    document.documentElement.classList.toggle('stack-mode', viewMode === 'stack')
  }, [viewMode])

  // Reflect mode changes coming from the Main process (e.g. another trigger, or
  // a cross-window broadcast). Functional setter avoids re-attaching the listener.
  useEffect(() => {
    if (!window.api) return
    const handleViewModeChange = (mode: WindowMode) => {
      setViewMode((prev) => {
        if (prev === mode) return prev
        // Main doesn't track the section scope; on returning to full, clear it.
        if (mode === 'full') { setListSectionId(null); persist('full', null) }
        else persist(mode, null)
        return mode
      })
    }
    window.api.receive('view-mode-changed', handleViewModeChange)

    // Boot reconciliation: adopt the Main process's authoritative mode. The
    // two persisted stores CAN diverge — dev + packaged app share
    // settings.json (Main) but NOT localStorage (this provider). A divergent
    // renderer paints the wrong layout, and its next switch-view-mode call is
    // swallowed by Main's idempotency guard (window keeps the wrong size).
    // Pull (not push) so no message races the listener registration above.
    let cancelled = false
    Promise.resolve(window.api.invoke('get-view-mode'))
      .then((raw) => {
        const result = raw as { mode?: WindowMode } | undefined
        const mode = result?.mode
        if (cancelled || (mode !== 'full' && mode !== 'list' && mode !== 'stack')) return
        handleViewModeChange(mode)
      })
      .catch(() => { /* older main without the handler — keep local mode */ })

    return () => {
      cancelled = true
      window.api?.removeAllListeners('view-mode-changed')
    }
  }, [persist])

  const value = useMemo<WindowModeContextValue>(() => ({
    viewMode,
    isListMode: viewMode === 'list',
    isStackMode: viewMode === 'stack',
    isCompactMode: viewMode !== 'full',
    listSectionId,
    switchToListMode,
    switchToStackMode,
    switchToFullMode,
    toggleListMode,
    toggleStackMode,
    alwaysOnTop,
    toggleAlwaysOnTop,
  }), [viewMode, listSectionId, switchToListMode, switchToStackMode, switchToFullMode, toggleListMode, toggleStackMode, alwaysOnTop, toggleAlwaysOnTop])

  return <WindowModeContext.Provider value={value}>{children}</WindowModeContext.Provider>
}

/**
 * Access the window mode. Returns a safe `full` default outside a provider
 * (e.g. isolated component tests / Storybook) so consumers never crash.
 */
export function useWindowMode(): WindowModeContextValue {
  const ctx = useContext(WindowModeContext)
  if (!ctx) {
    return {
      viewMode: 'full',
      isListMode: false,
      isStackMode: false,
      isCompactMode: false,
      listSectionId: null,
      switchToListMode: () => {},
      switchToStackMode: () => {},
      switchToFullMode: () => {},
      toggleListMode: () => {},
      toggleStackMode: () => {},
      alwaysOnTop: false,
      toggleAlwaysOnTop: () => {},
    }
  }
  return ctx
}
